import type { Texture } from 'pixi.js';
import { AudioManager } from './AudioManager';
import { HomeMonkey } from './HomeMonkey';

const MIN_BET = 1;
const BET_STEP = 1;

export class HomeScreen {
  private root: HTMLElement;
  private textures: Record<string, Texture>;
  private balanceEl: HTMLElement;
  private playBtn: HTMLButtonElement;
  private playBetEl: HTMLElement;
  private soundBtn: HTMLButtonElement;
  private betValueEl: HTMLElement;
  private betMinusBtn: HTMLButtonElement;
  private betPlusBtn: HTMLButtonElement;
  private presetBtns: HTMLButtonElement[];
  private monkey?: HomeMonkey;
  private audio: AudioManager;
  private tutorialBtn: HTMLButtonElement;
  private tutorialModal: HTMLElement;
  private tutorialCloseBtn: HTMLButtonElement;
  private oofModal: HTMLElement;
  private oofCloseBtn: HTMLButtonElement;
  private bet = 10;
  private balance = 0;
  private onPlayCb?: () => void;
  private onBetChangeCb?: (amount: number) => void;
  private hideTimer?: number;

  constructor(root: HTMLElement, textures: Record<string, Texture>, audio: AudioManager) {
    this.root = root;
    this.textures = textures;
    this.audio = audio;

    this.balanceEl = this.root.querySelector('[data-home-balance]') as HTMLElement;
    this.playBtn = this.root.querySelector('.home-play') as HTMLButtonElement;
    this.playBetEl = this.root.querySelector('[data-home-bet]') as HTMLElement;
    this.soundBtn = this.root.querySelector('[data-sound-btn]') as HTMLButtonElement;
    this.betValueEl = this.root.querySelector('[data-bet-value]') as HTMLElement;
    this.betMinusBtn = this.root.querySelector('[data-bet-minus]') as HTMLButtonElement;
    this.betPlusBtn = this.root.querySelector('[data-bet-plus]') as HTMLButtonElement;
    this.presetBtns = Array.from(this.root.querySelectorAll<HTMLButtonElement>('.bet-preset'));
    this.tutorialBtn = this.root.querySelector('[data-tutorial-btn]') as HTMLButtonElement;
    this.tutorialModal = document.querySelector('[data-tutorial-modal]') as HTMLElement;
    this.tutorialCloseBtn = this.tutorialModal.querySelector('[data-tutorial-close]') as HTMLButtonElement;
    this.oofModal = document.querySelector('[data-oof-modal]') as HTMLElement;
    this.oofCloseBtn = this.oofModal.querySelector('[data-oof-close]') as HTMLButtonElement;

    this.bind();
    this.renderBet();
    this.initMonkey();
  }

  private initMonkey() {
    if (window.matchMedia('(max-width: 640px)').matches) return;
    const host = this.root.querySelector('.home-monkey') as HTMLElement | null;
    if (!host) return;

    const tryMount = (attempts: number) => {
      const rect = host.getBoundingClientRect();
      if (rect.width >= 10 && rect.height >= 10) {
        this.monkey = new HomeMonkey();
        void this.monkey.mount(host, this.textures).catch((err) => {
          console.error('HomeMonkey mount failed:', err);
        });
        return;
      }
      if (attempts <= 0) {
        console.warn('HomeMonkey: host has zero size, giving up', rect);
        return;
      }
      requestAnimationFrame(() => tryMount(attempts - 1));
    };

    tryMount(30);
  }

  private renderBet() {
    const v = this.bet.toFixed(2);
    this.playBetEl.textContent = `BET $${v}`;
    this.betValueEl.textContent = `$${v}`;
    const maxBet = this.maxBet();
    for (const btn of this.presetBtns) {
      const raw = btn.dataset.bet || '';
      const presetValue = raw === 'max' ? maxBet : Number(raw);
      btn.classList.toggle('selected', presetValue === this.bet);
    }
    this.betMinusBtn.disabled = this.bet <= MIN_BET;
    this.betPlusBtn.disabled = this.bet >= maxBet;
  }

  private maxBet(): number {
    return Math.max(MIN_BET, Math.floor(this.balance));
  }

  private commitBet(next: number) {
    const clamped = Math.max(MIN_BET, Math.min(Math.floor(next), this.maxBet()));
    if (clamped === this.bet) {
      this.renderBet();
      return;
    }
    this.bet = clamped;
    this.renderBet();
    this.onBetChangeCb?.(this.bet);
  }

  setBet(amount: number) {
    this.bet = Math.max(MIN_BET, Math.floor(amount));
    this.renderBet();
  }

  onBetChange(cb: (amount: number) => void) {
    this.onBetChangeCb = cb;
  }

  private bind() {
    this.playBtn.addEventListener('click', () => {
      if (this.balance < this.bet || this.balance < MIN_BET) {
        this.showOutOfFunds();
        return;
      }
      this.hide();
      this.onPlayCb?.();
    });
    const applySoundState = (muted: boolean) => {
      this.soundBtn.classList.toggle('sound-on', !muted);
      this.soundBtn.classList.toggle('sound-off', muted);
    };
    applySoundState(this.audio.isMuted());
    this.audio.onChange(applySoundState);
    this.soundBtn.addEventListener('click', () => {
      this.audio.toggle();
    });
    this.betMinusBtn.addEventListener('click', () => this.commitBet(this.bet - BET_STEP));
    this.betPlusBtn.addEventListener('click', () => this.commitBet(this.bet + BET_STEP));
    for (const btn of this.presetBtns) btn.addEventListener('click', () => this.applyPreset(btn.dataset.bet || ''));

    this.tutorialBtn.addEventListener('click', () => this.setModal(this.tutorialModal, true));
    this.tutorialCloseBtn.addEventListener('click', () => this.setModal(this.tutorialModal, false));
    this.tutorialModal.addEventListener('click', (e) => {
      if (e.target === this.tutorialModal) this.setModal(this.tutorialModal, false);
    });

    this.oofCloseBtn.addEventListener('click', () => this.setModal(this.oofModal, false));
    this.oofModal.addEventListener('click', (e) => {
      if (e.target === this.oofModal) this.setModal(this.oofModal, false);
    });
  }

  private applyPreset(raw: string) {
    const target = raw === 'max' ? this.maxBet() : Number(raw);
    if (Number.isFinite(target)) this.commitBet(target);
  }

  private setModal(modal: HTMLElement, visible: boolean) {
    modal.classList.toggle('hidden', !visible);
    modal.setAttribute('aria-hidden', String(!visible));
  }

  showOutOfFunds() {
    this.setModal(this.oofModal, true);
  }

  setBalance(b: number) {
    this.balance = b;
    this.balanceEl.textContent = '$' + b.toFixed(2).replace(/\.00$/, '');
    if (this.bet > this.maxBet()) {
      this.commitBet(this.maxBet());
    } else {
      this.renderBet();
    }
  }

  onPlay(cb: () => void) {
    this.onPlayCb = cb;
  }

  hide() {
    if (this.root.classList.contains('fading')) return;
    this.root.classList.add('fading');
    if (this.hideTimer) window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      this.root.classList.add('hidden');
    }, 600);
  }

  show() {
    if (this.hideTimer) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }
    this.root.classList.remove('hidden');
    void this.root.offsetWidth;
    this.root.classList.remove('fading');
  }
}
